import { constants } from 'appConstants';
import { motion } from 'framer-motion';
import {
  ChatPageSidebar,
  ChatPageSidebarMobile,
  ChatPageContainer,
  ChatPageSidebarContainer,
  ChatPageMessagesContainer,
  BrandLogoChat,
  ChatAIInput,
  ChatMenuStack,
  MenuIcon,
  ChatContext,
  useChatForm,
  CHATAI_LOADING_STATUS,
} from '@ltvco/refresh-lib/v2';
import { MessagesContainer } from './components/MessagesContainer';
import { useContext, useEffect, useState } from 'react';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { RecentReports } from 'routes/Dashboard/RecentReports/RecentReports';
import { ChatPageContainerStack } from './components';

export const ChatPage = () => {
  const { messages, loadingStatus } = useContext(ChatContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSendMessage } = useChatForm(() => {});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [shouldCloseReport, setShouldCloseReport] = useState(false);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState<
    'center' | 'moving-up' | 'moving-left' | 'header'
  >('center');
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(false);
  const { isMobile, isMedium } = useScreenSize();

  const reportLibraryRoute = `${constants.links.chatAILink}/library`;
  const isReportLibraryOpen = location.pathname === reportLibraryRoute;
  // Show header menu when not on landing (mobile) and report is not open
  const shouldShowHeaderMenu = !(showLanding && isMobile) && !isReportOpen;

  useEffect(() => {
    document.body.classList.add('chat-page');

    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  const headerLogoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  // Handle transition from landing page to chat
  useEffect(() => {
    // Only trigger animation if there are messages AND we're on landing page
    // AND the last message is from the user (not assistant)
    if (messages.length > 0 && showLanding) {
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.role === 'user';

      if (isUserMessage) {
        // Start logo animation
        setIsLogoAnimating(true);
        setLogoAnimationPhase('moving-up');

        // After logo moves up, move it to the left
        setTimeout(() => {
          setLogoAnimationPhase('moving-left');
        }, 800);

        // After logo moves left, transition to chat
        setTimeout(() => {
          setLogoAnimationPhase('header');
          // Defer state updates to next event loop cycle to ensure 'header' animation renders first
          setTimeout(() => {
            setHasCompletedAnimation(true);
            setShowLanding(false);
            setIsLogoAnimating(false);
          }, 0);
        }, 1200);
      }
    } else if (messages.length === 0 && !showLanding) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
    }
  }, [messages.length, showLanding, messages]);

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleCloseReport = () => {
    setShouldCloseReport(true);
    setIsReportOpen(false);
  };

  const handleReportLibraryClick = () => {
    navigate(reportLibraryRoute);
    // Close mobile sidebar when opening report library
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    }
  };

  const handleCloseReportLibrary = () => {
    navigate(constants.links.chatAILink);
    setShouldCloseReport(true);
  };

  const handleShouldCloseReport = () => {
    setShouldCloseReport(false);
  };

  return (
    <ChatPageContainer>
      {!isMedium ? (
        <ChatPageSidebarContainer>
          <ChatPageSidebar
            onReportLibraryClick={handleReportLibraryClick}
            onCloseReportLibrary={handleCloseReportLibrary}
          />
        </ChatPageSidebarContainer>
      ) : (
        <>
          {shouldShowHeaderMenu && (
            <ChatMenuStack>
              <motion.div
                variants={headerLogoVariants}
                initial="hidden"
                animate={hasCompletedAnimation ? 'visible' : 'hidden'}
              >
                <BrandLogoChat
                  width="116px"
                  height="18px"
                  sx={{
                    transform: 'translateY(0) translateX(0) scale(0.7)',
                  }}
                />
              </motion.div>
              <MenuIcon onClick={handleMenuClick} />
            </ChatMenuStack>
          )}
          {(isSidebarOpen || isAnimatingOut) && (
            <ChatPageSidebarMobile
              isOpen={isSidebarOpen && !isAnimatingOut}
              setIsOpen={setIsSidebarOpen}
              onReportLibraryClick={handleReportLibraryClick}
              onCloseReportLibrary={handleCloseReportLibrary}
            />
          )}
        </>
      )}
      <ChatPageMessagesContainer isLanding={showLanding}>
        {isReportLibraryOpen ? (
          <ChatPageContainerStack>
            <RecentReports isInChatPage={true} />
          </ChatPageContainerStack>
        ) : (
          <MessagesContainer
            messages={messages}
            loadingStatus={loadingStatus}
            onReportStateChange={setIsReportOpen}
            shouldCloseReport={shouldCloseReport}
            onReportCloseHandled={handleShouldCloseReport}
            showLanding={showLanding}
            isLogoAnimating={isLogoAnimating}
            logoAnimationPhase={logoAnimationPhase}
            onSendMessage={handleSendMessage}
            sx={{
              ...(isMobile &&
                !isReportOpen && {
                  paddingTop: '0',
                }),
            }}
          />
        )}
      </ChatPageMessagesContainer>
      {!isReportLibraryOpen &&
        isMobile &&
        !showLanding &&
        messages.length > 0 && (
          <ChatAIInput
            isLanding={false}
            isAnimating={isLogoAnimating}
            animationPhase={logoAnimationPhase}
            isLoading={loadingStatus !== CHATAI_LOADING_STATUS.IDLE}
            handleReportClose={handleCloseReport}
          />
        )}
    </ChatPageContainer>
  );
};
